"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Great_Vibes, Cormorant_Garamond } from "next/font/google";
import SharePanel from "@/app/components/SharePanel";
import SessionBackLink from "@/app/components/SessionBackLink";
import {
  attachCameraStreamToVideo,
  getCameraErrorMessage,
  requestCameraStream,
} from "@/app/lib/cameraAccess";
import { drawWeddingFrameToCanvas, WeddingPhotoFrame } from "@/app/lib/photoFrame";
import { getAlbumShortPath } from "@/app/lib/shareLinks";

const greatVibes = Great_Vibes({
  subsets: ["latin"],
  weight: "400",
});

const cormorant = Cormorant_Garamond({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

type SavedPhoto = {
  id: string;
  src: string;
  title: string;
  createdAt: string;
  source: "framed";
};

const countdownStart = 3;
const flashBeforeCaptureMs = 140;
const flashAfterCaptureMs = 340;
const autoCaptureStillMs = 5000;
const motionSampleMs = 250;
const motionCanvasWidth = 48;
const motionCanvasHeight = 64;
const motionDifferenceThreshold = 7;

function stopVideoStream(videoElement: HTMLVideoElement | null) {
  const stream = videoElement?.srcObject as MediaStream | null;
  stream?.getTracks().forEach((track) => track.stop());

  if (videoElement) {
    videoElement.srcObject = null;
  }
}

export default function FramedPhotoPage() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const motionCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const countdownTimersRef = useRef<number[]>([]);
  const captureBusyRef = useRef(false);
  const startingCameraRef = useRef(false);
  const previousMotionFrameRef = useRef<Uint8ClampedArray | null>(null);
  const stillSinceRef = useRef<number | null>(null);
  const lastMotionSampleRef = useRef(0);

  const [streaming, setStreaming] = useState(false);
  const [captured, setCaptured] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isStartingCamera, setIsStartingCamera] = useState(false);
  const [isCaptureBusy, setIsCaptureBusy] = useState(false);
  const [countdownValue, setCountdownValue] = useState<number | null>(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const [autoCaptureEnabled, setAutoCaptureEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const clearCountdownTimers = useCallback(() => {
    countdownTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    countdownTimersRef.current = [];
  }, []);

  const resetMotionTracking = useCallback(() => {
    previousMotionFrameRef.current = null;
    stillSinceRef.current = null;
    lastMotionSampleRef.current = 0;
  }, []);

  const startCamera = useCallback(async () => {
    const videoElement = videoRef.current;
    if (!videoElement || startingCameraRef.current) return;

    try {
      startingCameraRef.current = true;
      setIsStartingCamera(true);
      setErrorMessage("");
      setStreaming(false);
      stopVideoStream(videoElement);

      const stream = await requestCameraStream();
      await attachCameraStreamToVideo(videoElement, stream);
      setStreaming(true);
    } catch (error) {
      console.error(error);
      stopVideoStream(videoElement);
      setStreaming(false);
      setErrorMessage(getCameraErrorMessage(error));
    } finally {
      startingCameraRef.current = false;
      setIsStartingCamera(false);
    }
  }, []);

  useEffect(() => {
    void startCamera();
    const videoElement = videoRef.current;

    return () => {
      clearCountdownTimers();
      stopVideoStream(videoElement);
    };
  }, [clearCountdownTimers, startCamera]);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      setErrorMessage("Camera not available. Try opening this page in Safari if Chrome has issues.");
      return false;
    }

    const width = video.videoWidth || 1080;
    const height = video.videoHeight || 1440;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return false;

    drawWeddingFrameToCanvas(ctx, video, width, height);

    const dataUrl = canvas.toDataURL("image/png");
    setCaptured(dataUrl);
    return true;
  }, []);

  const startCountdown = useCallback(() => {
    if (!streaming || captured || captureBusyRef.current) return;

    clearCountdownTimers();
    captureBusyRef.current = true;
    setIsCaptureBusy(true);
    setErrorMessage("");
    setCountdownValue(countdownStart);
    setIsFlashing(false);
    resetMotionTracking();

    const schedule = (delay: number, callback: () => void) => {
      const timer = window.setTimeout(callback, delay);
      countdownTimersRef.current.push(timer);
    };

    schedule(1000, () => setCountdownValue(2));
    schedule(2000, () => setCountdownValue(1));
    schedule(3000, () => {
      setCountdownValue(null);
      setIsFlashing(true);

      schedule(flashBeforeCaptureMs, () => {
        capturePhoto();

        schedule(flashAfterCaptureMs, () => {
          setIsFlashing(false);
          captureBusyRef.current = false;
          setIsCaptureBusy(false);
          countdownTimersRef.current = [];
        });
      });
    });
  }, [capturePhoto, captured, clearCountdownTimers, resetMotionTracking, streaming]);

  useEffect(() => {
    resetMotionTracking();

    if (!autoCaptureEnabled || !streaming || captured) return;

    let animationFrameId: number;
    const canvas = motionCanvasRef.current ?? document.createElement("canvas");
    motionCanvasRef.current = canvas;
    canvas.width = motionCanvasWidth;
    canvas.height = motionCanvasHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    if (!ctx) return;

    const sampleMotion = (timestamp: number) => {
      if (timestamp - lastMotionSampleRef.current >= motionSampleMs) {
        lastMotionSampleRef.current = timestamp;

        const video = videoRef.current;
        if (video && video.readyState >= 2 && !captureBusyRef.current) {
          try {
            ctx.drawImage(video, 0, 0, motionCanvasWidth, motionCanvasHeight);
            const currentFrame = ctx.getImageData(0, 0, motionCanvasWidth, motionCanvasHeight).data;
            const previousFrame = previousMotionFrameRef.current;

            if (previousFrame && previousFrame.length === currentFrame.length) {
              let totalDifference = 0;
              let comparedChannels = 0;

              for (let index = 0; index < currentFrame.length; index += 16) {
                totalDifference += Math.abs(currentFrame[index] - previousFrame[index]);
                totalDifference += Math.abs(currentFrame[index + 1] - previousFrame[index + 1]);
                totalDifference += Math.abs(currentFrame[index + 2] - previousFrame[index + 2]);
                comparedChannels += 3;
              }

              const averageDifference = totalDifference / comparedChannels;
              const now = Date.now();

              if (averageDifference < motionDifferenceThreshold) {
                stillSinceRef.current ??= now;

                if (now - stillSinceRef.current >= autoCaptureStillMs) {
                  resetMotionTracking();
                  startCountdown();
                  return;
                }
              } else {
                stillSinceRef.current = null;
              }
            } else {
              stillSinceRef.current = Date.now();
            }

            previousMotionFrameRef.current = new Uint8ClampedArray(currentFrame);
          } catch (error) {
            console.error(error);
            resetMotionTracking();
          }
        } else {
          resetMotionTracking();
        }
      }

      animationFrameId = window.requestAnimationFrame(sampleMotion);
    };

    animationFrameId = window.requestAnimationFrame(sampleMotion);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      resetMotionTracking();
    };
  }, [autoCaptureEnabled, captured, resetMotionTracking, startCountdown, streaming]);

  function resetPhoto() {
    clearCountdownTimers();
    captureBusyRef.current = false;
    setIsCaptureBusy(false);
    setCountdownValue(null);
    setIsFlashing(false);
    setCaptured(null);
    resetMotionTracking();
  }

  function deletePhoto() {
    clearCountdownTimers();
    captureBusyRef.current = false;
    setIsCaptureBusy(false);
    setCountdownValue(null);
    setIsFlashing(false);
    setCaptured(null);
    resetMotionTracking();
  }

  function saveToLocalGallery(photoDataUrl: string) {
    const storageKey = "weddingPhotoUploads";

    const existingRaw = window.localStorage.getItem(storageKey);
    const existing: SavedPhoto[] = existingRaw ? JSON.parse(existingRaw) : [];

    const newPhoto: SavedPhoto = {
      id: `framed-${Date.now()}`,
      src: photoDataUrl,
      title: `Framed Photo ${existing.length + 1}`,
      createdAt: new Date().toISOString(),
      source: "framed",
    };

    const updated = [newPhoto, ...existing];
    window.localStorage.setItem(storageKey, JSON.stringify(updated));
  }

  function saveFramedPhoto() {
    if (!captured) return;

    try {
      setIsSaving(true);
      saveToLocalGallery(captured);
      alert("Framed photo saved to the app gallery on this device. You can now share it or open the framed gallery.");
    } catch (error) {
      console.error(error);
      alert("There was a problem saving the photo.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <main className={`min-h-screen bg-[#f6f4ef] px-4 py-6 text-slate-800 sm:px-6 lg:px-8 ${cormorant.className}`}>
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <SessionBackLink />

          <h1
            className={`${greatVibes.className} wedding-gold-halo text-4xl text-[#1f4e79] sm:text-5xl`}
          >
            Framed Photo
          </h1>

          <div className="w-20" />
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="overflow-hidden rounded-[1.75rem] border border-[#decf9b] bg-white shadow-sm">
              <div className="relative aspect-[3/4] bg-black">
                {!captured ? (
                  <WeddingPhotoFrame
                    videoRef={videoRef}
                    captured={null}
                    imageAlt="Captured framed photo preview"
                  />
                ) : (
                  <Image
                    src={captured}
                    alt="Captured framed photo preview"
                    fill
                    unoptimized
                    className="object-contain"
                  />
                )}

                {!captured && !streaming ? (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[rgba(9,7,5,0.74)] px-6 text-center text-white">
                    <p className="max-w-sm text-base leading-7">
                      {isStartingCamera
                        ? "Starting the camera..."
                        : errorMessage || "Please allow camera access to use the photo frame."}
                    </p>
                    <button
                      type="button"
                      onClick={startCamera}
                      disabled={isStartingCamera}
                      className="mt-5 inline-flex min-h-12 items-center justify-center rounded-full bg-[#f8f5ed] px-6 py-3 text-xs font-semibold uppercase tracking-[.14em] text-[#1f4e79] transition hover:bg-white disabled:opacity-60"
                    >
                      {isStartingCamera ? "Starting..." : "Enable Camera"}
                    </button>
                  </div>
                ) : null}

                {countdownValue !== null ? (
                  <div className="absolute inset-0 z-30 grid place-items-center bg-[radial-gradient(circle_at_center,rgba(31,78,121,0.30),rgba(9,7,5,0.76))] text-white">
                    <span
                      key={countdownValue}
                      className="text-[clamp(7rem,28vw,14rem)] font-bold leading-none text-[#fffaf0] [text-shadow:0_0_18px_rgba(241,207,122,0.92),0_0_52px_rgba(241,207,122,0.45)]"
                      style={{ animation: "booth-countdown-pop 950ms ease both" }}
                    >
                      {countdownValue}
                    </span>
                  </div>
                ) : null}

                {isFlashing ? (
                  <div
                    className="absolute inset-0 z-40 bg-[#fffaf0]"
                    style={{ animation: "booth-flash 520ms ease both" }}
                  />
                ) : null}
              </div>
            </div>

            <div className="rounded-[1.75rem] border border-[#decf9b] bg-white px-6 py-7 shadow-sm">
              <div className="rounded-2xl border border-[#ead8a0] bg-[#fffdfa] px-4 py-4">
                <p className="text-sm leading-6 text-[#686861]">
                  This wedding page needs access to your camera and photo library so you can upload memories and use the photo frame.
                </p>
                <button
                  type="button"
                  onClick={startCamera}
                  disabled={isStartingCamera || streaming}
                  className="mt-4 inline-flex min-h-12 w-full items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#f8f5ed] px-5 py-3 text-xs font-semibold uppercase tracking-[.14em] text-[#1f4e79] transition hover:bg-[#f2ecdf] disabled:opacity-60"
                >
                  {streaming ? "Camera Enabled" : isStartingCamera ? "Starting Camera..." : "Enable Camera"}
                </button>
                {errorMessage ? (
                  <p className="mt-3 text-sm leading-6 text-red-700" aria-live="polite">
                    {errorMessage}
                  </p>
                ) : null}
              </div>

              <p className="mt-6 text-[11px] uppercase tracking-[0.28em] text-[#9a9a92]">
                Wedding Keepsake
              </p>

              <h2 className="mt-3 text-4xl font-medium text-[#1f3f6d]">
                Capture your framed moment
              </h2>

              <p className="mt-4 text-base leading-8 text-[#686861]">
                Position yourself inside the frame, capture your photo, preview it, then save it into the app gallery.
              </p>

              <div className="mt-8 flex flex-col gap-4">
                {!captured ? (
                  <>
                    <button
                      type="button"
                      onClick={startCountdown}
                      disabled={!streaming || isCaptureBusy}
                      className="inline-flex min-h-14 items-center justify-center rounded-full bg-[#1f4e79] px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-white transition hover:bg-[#173d61] disabled:opacity-50"
                    >
                      {isCaptureBusy ? "Countdown Started" : "Capture Photo"}
                    </button>

                    <p className="text-center text-sm leading-6 text-[#686861] sm:text-base">
                      Line yourself up in the frame, tap Capture Photo, then wait for the countdown. Hold still until the photo is taken.
                    </p>

                    <label className="flex items-center justify-between gap-4 rounded-2xl border border-[#ead8a0] bg-[#fffdfa] px-4 py-3 text-[#1f4e79]">
                      <span className="text-sm font-semibold uppercase tracking-[.12em]">
                        Auto capture when still
                      </span>
                      <input
                        type="checkbox"
                        checked={autoCaptureEnabled}
                        onChange={(event) => {
                          setAutoCaptureEnabled(event.target.checked);
                          resetMotionTracking();
                        }}
                        className="h-5 w-5 accent-[#1f4e79]"
                      />
                    </label>

                    {autoCaptureEnabled ? (
                      <p className="text-center text-sm leading-6 text-[#686861]" aria-live="polite">
                        Hold still and we&apos;ll take the photo automatically.
                      </p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={saveFramedPhoto}
                      disabled={isSaving}
                      className="inline-flex min-h-14 items-center justify-center rounded-full bg-[#1f4e79] px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-white transition hover:bg-[#173d61] disabled:opacity-50"
                    >
                      {isSaving ? "Saving..." : "Save to Gallery"}
                    </button>

                    <button
                      type="button"
                      onClick={resetPhoto}
                      className="inline-flex min-h-14 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#f8f5ed] px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                    >
                      Retake Photo
                    </button>

                    <button
                      type="button"
                      onClick={deletePhoto}
                      className="inline-flex min-h-14 items-center justify-center rounded-full border border-red-200 bg-red-50 px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-red-700 transition hover:bg-red-100"
                    >
                      Delete Photo
                    </button>

                    <SharePanel
                      title="Framed Wedding Photo"
                      shortPath={getAlbumShortPath("framed")}
                      imageSrc={captured}
                    />

                    <Link
                      href="/photos?filter=framed"
                      className="inline-flex min-h-14 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                    >
                      View Framed Gallery
                    </Link>
                  </>
                )}
              </div>

              <canvas ref={canvasRef} className="hidden" />
            </div>
          </div>
      </div>
    </main>
  );
}
